import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';
import { TagComponent } from 'src/shared/components/tag/tag.component';

export type PropertyCardTone = 'success' | 'warning' | 'error' | 'neutral';

export type PropertyCardSeverity = 'critical' | 'warning' | 'opportunity' | 'ok';

export interface PropertyCardChip {
  label: string;
  tone: PropertyCardTone;
  detail?: string;
}

export interface PropertyCardPolicyView {
  insuranceId: number;
  title: string;
  metaLine: string;
  chips: PropertyCardChip[];
  savingsLabel?: string;
}

export interface PropertyCardView {
  addressId: number;
  name: string;
  subtitle: string;
  color: string;
  severity: PropertyCardSeverity;
  pill: PropertyCardChip;
  policies: PropertyCardPolicyView[];
  estimateLabel?: string;
  /**
   * True when this card's pricing came from SteadilyMockService rather than the estimate
   * proxy — either the fetch failed or it hasn't run yet. Shown so a sample number is
   * never mistaken for a real quote.
   */
  pricingIsSample?: boolean;
  /** Set for the bucket holding policies with no property, which has no real address id. */
  isUnassigned?: boolean;
}

export interface PropertyCardEvent {
  action: 'get_quote' | 'add_policy' | 'edit_policy';
  addressId: number;
  insuranceId?: number;
}

/**
 * One card per property: its policies with status/risk chips, or the Steadily
 * estimate CTA when nothing is on file. Purely presentational — the view models
 * are prebuilt so the grid stays glanceable and template-logic free.
 */
@Component({
  selector: 'insurance-property-cards',
  templateUrl: './insurance-property-cards.component.html',
  styleUrls: ['./insurance-property-cards.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, IonicModule, TidyUiComponentsModule, SharedModule, TagComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class InsurancePropertyCardsComponent implements AfterViewInit, OnDestroy {

  private destroyRef = inject(DestroyRef);

  readonly cards = input<PropertyCardView[]>([]);

  readonly cardAction = output<PropertyCardEvent>();
  /** Emits a card's addressId the first time it scrolls into view, so the page can
   *  lazy-load that property's Steadily estimate on demand instead of all at once. */
  readonly cardVisible = output<number>();

  @ViewChildren('cardEl', { read: ElementRef })
  private cardEls!: QueryList<ElementRef<HTMLElement>>;

  private observer?: IntersectionObserver;
  private observed = new WeakSet<Element>();

  ngAfterViewInit(): void {
    // Prefetch a little before the card is actually on screen for a seamless reveal.
    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) { return; }
          this.observer?.unobserve(entry.target);
          const id = Number((entry.target as HTMLElement).dataset.addressId);
          if (!Number.isNaN(id)) { this.cardVisible.emit(id); }
        });
      },
      { rootMargin: '200px' }
    );
    this.observeCards();
    this.cardEls.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.observeCards());
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private observeCards(): void {
    this.cardEls?.forEach(ref => {
      const el = ref.nativeElement;
      if (this.observed.has(el)) { return; }
      this.observed.add(el);
      this.observer?.observe(el);
    });
  }

  onGetQuote(card: PropertyCardView): void {
    this.cardAction.emit({ action: 'get_quote', addressId: card.addressId });
  }

  onEditPolicy(card: PropertyCardView, policy: PropertyCardPolicyView): void {
    this.cardAction.emit({ action: 'edit_policy', addressId: card.addressId, insuranceId: policy.insuranceId });
  }

  /** Lives inside the clickable policy row, so keep the click from reaching it. */
  onViewQuote(card: PropertyCardView, policy: PropertyCardPolicyView, event: Event): void {
    event.stopPropagation();
    this.cardAction.emit({
      action: 'get_quote',
      addressId: card.addressId,
      insuranceId: policy.insuranceId,
    });
  }
}
